import React from "react";
import "./blogBody.css";

// Renders the Lexical block JSON produced by the super-admin's blog editor
// (bodyBlocks), the same structure aliff-next-website renders on its own
// blog detail page — kept in sync with that renderer's node types.

const IS_BOLD = 1;
const IS_ITALIC = 2;
const IS_STRIKETHROUGH = 4;
const IS_UNDERLINE = 8;
const IS_CODE = 16;
const IS_SUBSCRIPT = 32;
const IS_SUPERSCRIPT = 64;

function LexicalText({ node }) {
  const fmt = node.format ?? 0;
  let content = node.text;

  if (fmt & IS_CODE) content = <code className="blog-body-code">{content}</code>;
  if (fmt & IS_BOLD) content = <strong>{content}</strong>;
  if (fmt & IS_ITALIC) content = <em>{content}</em>;
  if (fmt & IS_UNDERLINE) content = <u>{content}</u>;
  if (fmt & IS_STRIKETHROUGH) content = <s>{content}</s>;
  if (fmt & IS_SUBSCRIPT) content = <sub>{content}</sub>;
  if (fmt & IS_SUPERSCRIPT) content = <sup>{content}</sup>;

  return <>{content}</>;
}

function InlineChildren({ children }) {
  return (
    <>
      {(children ?? []).map((child, i) => {
        if (child.type === "text") return <LexicalText key={i} node={child} />;
        if (child.type === "linebreak") return <br key={i} />;
        if (child.type === "link")
          return (
            <a
              key={i}
              href={child.url}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-body-link"
            >
              <InlineChildren children={child.children ?? []} />
            </a>
          );
        return <InlineChildren key={i} children={child.children ?? []} />;
      })}
    </>
  );
}

function HeadingNode({ node }) {
  const tag = node.tag ?? "h2";
  return React.createElement(
    tag,
    { className: `blog-body-${tag}` },
    <InlineChildren children={node.children ?? []} />
  );
}

function ParagraphNode({ node }) {
  if (!node.children?.length) return <div className="blog-body-spacer" />;
  return (
    <p className="blog-body-paragraph">
      <InlineChildren children={node.children} />
    </p>
  );
}

function QuoteNode({ node }) {
  return (
    <blockquote className="blog-body-quote">
      <InlineChildren children={node.children ?? []} />
    </blockquote>
  );
}

function ListNode({ node }) {
  const isBullet = node.listType === "bullet";
  const isCheck = node.listType === "check";
  const items = node.children ?? [];

  if (isCheck) {
    return (
      <div className="blog-body-checklist">
        {items.map((item, i) => (
          <div className="blog-body-checklist-item" key={i}>
            <span className="blog-body-check-icon">
              <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                <path
                  d="M1 4L4.5 7.5L11 1"
                  stroke="#4caf50"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>
              <InlineChildren children={item.children ?? []} />
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (isBullet) {
    return (
      <ul className="blog-body-bullet-list">
        {items.map((item, i) => (
          <li key={i}>
            <span>
              <InlineChildren children={item.children ?? []} />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ol className="blog-body-ordered-list">
      {items.map((item, i) => (
        <li key={i}>
          <span className="blog-body-ordered-index">{item.value ?? i + 1}.</span>
          <span>
            <InlineChildren children={item.children ?? []} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function CodeBlockNode({ node }) {
  const text = (node.children ?? []).map((c) => c.text ?? "").join("");
  return (
    <pre className="blog-body-code-block">
      <code>{text}</code>
    </pre>
  );
}

function TableCellNode({ node }) {
  const isHeader = (node.headerState ?? 0) !== 0;
  const Tag = isHeader ? "th" : "td";
  const inlineContent = (node.children ?? []).flatMap((p) => p.children ?? []);
  return (
    <Tag colSpan={node.colSpan ?? 1} rowSpan={node.rowSpan ?? 1}>
      <InlineChildren children={inlineContent} />
    </Tag>
  );
}

function TableNode({ node }) {
  return (
    <div className="blog-body-table-wrap">
      <table className="blog-body-table">
        <tbody>
          {(node.children ?? []).map((row, i) => (
            <tr key={i}>
              {(row.children ?? []).map((cell, j) => (
                <TableCellNode key={j} node={cell} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderNode(node, index) {
  switch (node.type) {
    case "heading":
      return <HeadingNode key={index} node={node} />;
    case "paragraph":
      return <ParagraphNode key={index} node={node} />;
    case "quote":
      return <QuoteNode key={index} node={node} />;
    case "list":
      return <ListNode key={index} node={node} />;
    case "code":
      return <CodeBlockNode key={index} node={node} />;
    case "table":
      return <TableNode key={index} node={node} />;
    case "horizontalrule":
      return <hr key={index} className="blog-body-hr" />;
    default:
      return null;
  }
}

const BlogBodyRenderer = ({ bodyBlocks }) => {
  const children = bodyBlocks?.root?.children ?? [];
  if (children.length === 0) return null;

  return <div className="blog-body-blocks">{children.map(renderNode)}</div>;
};

export default BlogBodyRenderer;
